import React, { useState, useEffect } from 'react';
import { getCampaigns, deleteCampaign } from '../../services/campaignService';
import CampaignForm from './CampaignForm';
import { auth } from '../../firebase/firebase'; // Add this import for auth

const EmailCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      // Check if the user is logged in
      if (!auth.currentUser) {
        console.error("User not authenticated");
        setError('Please log in to view your campaigns.');
        return;
      }
      
      // If authenticated, try to fetch campaigns
      fetchCampaigns();
    };
    
    checkAuth();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const campaignsData = await getCampaigns();
      setCampaigns(campaignsData);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      if (err.message === 'User not authenticated') {
        setError('Please log in to view your campaigns.');
      } else if (err.code === 'permission-denied') {
        setError('You do not have permission to access this data.');
      } else {
        setError(`Failed to load campaigns: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCampaign = (newCampaign) => {
    setCampaigns([newCampaign, ...campaigns]);
    setIsAddModalOpen(false);
  };

  const handleEditCampaign = (updatedCampaign) => {
    setCampaigns(
      campaigns.map(campaign => 
        campaign.id === updatedCampaign.id ? updatedCampaign : campaign
      )
    );
    setIsEditModalOpen(false);
    setCurrentCampaign(null);
  };

  const openEditModal = (campaign) => {
    setCurrentCampaign(campaign);
    setIsEditModalOpen(true);
  };

  const confirmDelete = (campaign) => {
    setCurrentCampaign(campaign);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCampaign = async () => {
    try {
      await deleteCampaign(currentCampaign.id);
      setCampaigns(campaigns.filter(campaign => campaign.id !== currentCampaign.id));
      setIsDeleteModalOpen(false);
      setCurrentCampaign(null);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError('Failed to delete campaign.');
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const searchLower = searchTerm.toLowerCase();
    return (
      campaign.title?.toLowerCase().includes(searchLower) ||
      campaign.subject?.toLowerCase().includes(searchLower)
    );
  });

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  // Empty state
  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">E-postkampanjer</h3>
            <p className="mt-1 text-sm text-gray-500">Skapa och hantera dina e-postkampanjer.</p>
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Ny kampanj
          </button>
        </div>
        
        <div className="p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Inga kampanjer ännu</h3>
          <p className="mt-1 text-sm text-gray-500">Kom igång genom att skapa en ny e-postkampanj.</p>
          <div className="mt-6">
            <button 
              type="button" 
              onClick={() => setIsAddModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Ny kampanj
            </button>
          </div>
        </div>

        {/* Campaign Form Modal */}
        <CampaignForm
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddCampaign}
          title="Skapa ny kampanj"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">E-postkampanjer</h3>
          <p className="mt-1 text-sm text-gray-500">Skapa och hantera dina e-postkampanjer.</p>
        </div>
        <button 
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Ny kampanj
        </button>
      </div>
      
      <div className="px-4 py-3 bg-gray-50 sm:px-6">
        <div className="flex items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Sök kampanjer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 p-4">
          {filteredCampaigns.map((campaign) => (
            <div key={campaign.id} className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900 truncate">{campaign.title}</h3>
                <p className="mt-1 text-sm text-gray-500 truncate">{campaign.subject}</p>
              </div>
              <div className="px-4 py-4 sm:px-6">
                <p className="text-sm text-gray-500">
                  Skapad: {formatDate(campaign.createdAt)}
                </p>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => openEditModal(campaign)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Redigera
                  </button>
                  <button
                    onClick={() => confirmDelete(campaign)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Campaign Modal - Translate title */}
      <CampaignForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddCampaign}
        title="Skapa ny kampanj"
      />

      {/* Edit Campaign Modal - Translate title */}
      {currentCampaign && (
        <CampaignForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setCurrentCampaign(null);
          }}
          onSave={handleEditCampaign}
          campaign={currentCampaign}
          title="Redigera kampanj"
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen">
            <div className="fixed inset-0 bg-black opacity-30" onClick={() => setIsDeleteModalOpen(false)}></div>
            
            <div className="relative bg-white rounded-lg max-w-md w-full mx-auto p-6 shadow-xl">
              <h2 className="text-lg font-medium text-gray-900">
                Ta bort kampanj
              </h2>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Är du säker på att du vill ta bort "{currentCampaign?.title}"? Denna åtgärd kan inte ångras.
                </p>
              </div>

              <div className="mt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={handleDeleteCampaign}
                >
                  Ta bort
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaigns;
